import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, X, Loader2, FolderOpen, Tag, Inbox, MessageSquare, KeyRound, Euro } from "lucide-react";
import { Logo } from "@/components/Logo";
import PricingEditor from "@/components/PricingEditor";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";

const categories = [
  { value: "siti-web", label: "Sito Web" },
  { value: "social", label: "Social" },
  { value: "marketing", label: "Marketing" },
];

const emptyProject = { title: "", category: "siti-web", client: "", year: "", description: "", image_url: "", tags: [], featured: false, order: 0 };

function ProjectModal({ project, onClose, onSaved }) {
  const [form, setForm] = useState(project || emptyProject);
  const [saving, setSaving] = useState(false);
  const isEdit = !!project?.id;

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const save = async () => {
    if (!form.title) { toast.error("Inserisci un titolo"); return; }
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (isEdit) await api.put(`/admin/projects/${project.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success(isEdit ? "Progetto aggiornato" : "Progetto creato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" data-testid="project-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-display font-bold text-xl text-ink">{isEdit ? "Modifica progetto" : "Nuovo progetto"}</h3>
          <button onClick={onClose} data-testid="project-modal-close"><X className="text-ink/50" /></button>
        </div>
        <div className="space-y-4">
          <input name="title" value={form.title} onChange={handle} placeholder="Titolo *" data-testid="project-title" className="w-full rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
          <div className="grid grid-cols-2 gap-4">
            <select name="category" value={form.category} onChange={handle} data-testid="project-category" className="rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral">
              {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <input name="order" type="number" value={form.order} onChange={handle} placeholder="Ordine" data-testid="project-order" className="rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input name="client" value={form.client} onChange={handle} placeholder="Cliente" data-testid="project-client" className="rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
            <input name="year" value={form.year} onChange={handle} placeholder="Anno" data-testid="project-year" className="rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
          </div>
          <input name="image_url" value={form.image_url} onChange={handle} placeholder="URL immagine" data-testid="project-image" className="w-full rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
          <textarea name="description" value={form.description} onChange={handle} rows={3} placeholder="Descrizione" data-testid="project-description" className="w-full rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral resize-none" />
          <label className="flex items-center gap-2 text-sm text-ink">
            <input name="featured" type="checkbox" checked={form.featured} onChange={handle} data-testid="project-featured" /> In evidenza
          </label>
        </div>
        <button onClick={save} disabled={saving} data-testid="project-save" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-coral text-white rounded-full py-3.5 font-semibold hover:bg-[#e04047] transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : "Salva"}
        </button>
      </div>
    </div>
  );
}

function PackageModal({ pkg, onClose, onSaved }) {
  const [form, setForm] = useState({ ...pkg, features: pkg.features.join("\n") });
  const [saving, setSaving] = useState(false);

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, price: form.price, period: form.period, tagline: form.tagline,
        highlighted: form.highlighted, order: Number(form.order) || 0,
        features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
      };
      await api.put(`/admin/packages/${pkg.id}`, payload);
      toast.success("Pacchetto aggiornato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" data-testid="package-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-display font-bold text-xl text-ink">Modifica pacchetto {form.name}</h3>
          <button onClick={onClose} data-testid="package-modal-close"><X className="text-ink/50" /></button>
        </div>
        <div className="space-y-4">
          <input name="name" value={form.name} onChange={handle} placeholder="Nome" data-testid="package-name" className="w-full rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
          <div className="grid grid-cols-2 gap-4">
            <input name="price" value={form.price} onChange={handle} placeholder="Prezzo (vuoto = su richiesta)" data-testid="package-price" className="rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
            <input name="period" value={form.period} onChange={handle} placeholder="Periodo (es. mese)" data-testid="package-period" className="rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
          </div>
          <input name="tagline" value={form.tagline} onChange={handle} placeholder="Tagline" data-testid="package-tagline" className="w-full rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
          <div>
            <label className="block text-sm text-ink/60 mb-1">Caratteristiche (una per riga)</label>
            <textarea name="features" value={form.features} onChange={handle} rows={6} data-testid="package-features" className="w-full rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral resize-none" />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input name="highlighted" type="checkbox" checked={form.highlighted} onChange={handle} data-testid="package-highlighted" /> Evidenziato (più scelto)
          </label>
        </div>
        <button onClick={save} disabled={saving} data-testid="package-save" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-coral text-white rounded-full py-3.5 font-semibold hover:bg-[#e04047] transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : "Salva"}
        </button>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (next.length < 8) { toast.error("La nuova password deve avere almeno 8 caratteri"); return; }
    if (next !== confirm) { toast.error("Le password non coincidono"); return; }
    setSaving(true);
    try {
      await changePassword(current, next);
      toast.success("Password aggiornata con successo");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" data-testid="change-password-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-display font-bold text-xl text-ink">Cambia password</h3>
          <button onClick={onClose} data-testid="change-password-close"><X className="text-ink/50" /></button>
        </div>
        <div className="space-y-4">
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Password attuale" data-testid="current-password" className="w-full rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Nuova password (min 8 caratteri)" data-testid="new-password" className="w-full rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Conferma nuova password" data-testid="confirm-password" className="w-full rounded-xl border border-ink/15 px-4 py-3 outline-none focus:border-coral" />
        </div>
        <button onClick={save} disabled={saving} data-testid="change-password-save" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-coral text-white rounded-full py-3.5 font-semibold hover:bg-[#e04047] transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : "Aggiorna password"}
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [packages, setPackages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [projectModal, setProjectModal] = useState(null);
  const [packageModal, setPackageModal] = useState(null);
  const [pwModal, setPwModal] = useState(false);

  const loadAll = useCallback(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Eliminare questo progetto?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Progetto eliminato");
    loadAll();
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    await api.delete(`/admin/contacts/${id}`);
    toast.success("Messaggio eliminato");
    loadAll();
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Eliminare questo lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Lead eliminato");
    loadAll();
  };

  const tabs = [
    { key: "projects", label: "Portfolio", icon: FolderOpen, count: projects.length },
    { key: "packages", label: "Pacchetti", icon: Tag, count: packages.length },
    { key: "pricing", label: "Prezzi sito", icon: Euro, count: null },
    { key: "leads", label: "Lead chatbot", icon: MessageSquare, count: leads.length },
    { key: "contacts", label: "Messaggi", icon: Inbox, count: contacts.length },
  ];

  return (
    <div className="min-h-screen bg-beige" data-testid="admin-page">
      <header className="bg-ink text-beige">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo reverse size="text-xl" />
            <span className="text-beige/40 text-sm hidden sm:inline">· Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-beige/60 text-sm hidden sm:inline" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={() => setPwModal(true)} data-testid="admin-change-password" className="inline-flex items-center gap-2 text-sm font-medium hover:text-coral transition-colors">
              <KeyRound size={18} /> <span className="hidden sm:inline">Password</span>
            </button>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-medium hover:text-coral transition-colors">
              <LogOut size={18} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap gap-3 mb-8">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.key ? "bg-coral text-white" : "bg-white text-ink hover:bg-ink hover:text-beige"
              }`}
            >
              <t.icon size={16} /> {t.label}
              {t.count != null && (
                <span className={`ml-1 rounded-full px-2 text-xs ${tab === t.key ? "bg-white/20" : "bg-ink/10"}`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* PROJECTS */}
        {tab === "projects" && (
          <div data-testid="admin-projects">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-display font-bold text-2xl text-ink">Progetti portfolio</h2>
              <button onClick={() => setProjectModal({})} data-testid="admin-add-project" className="inline-flex items-center gap-2 bg-coral text-white rounded-full px-5 py-2.5 font-semibold hover:bg-[#e04047] transition-colors">
                <Plus size={18} /> Nuovo
              </button>
            </div>
            {projects.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center text-ink/50">Nessun progetto. Aggiungine uno!</div>
            ) : (
              <div className="bg-white rounded-2xl overflow-hidden divide-y divide-ink/5">
                {projects.map((p) => (
                  <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-project-row-${p.id}`}>
                    <div className="h-12 w-12 rounded-lg bg-beige overflow-hidden shrink-0">
                      {p.image_url && <img src={p.image_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-ink truncate">{p.title}</div>
                      <div className="text-xs text-ink/40">{p.category}{p.client ? ` · ${p.client}` : ""}</div>
                    </div>
                    <button onClick={() => setProjectModal(p)} data-testid={`admin-edit-project-${p.id}`} className="p-2 text-ink/50 hover:text-coral"><Pencil size={18} /></button>
                    <button onClick={() => deleteProject(p.id)} data-testid={`admin-delete-project-${p.id}`} className="p-2 text-ink/50 hover:text-coral"><Trash2 size={18} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PACKAGES */}
        {tab === "packages" && (
          <div data-testid="admin-packages">
            <h2 className="font-display font-bold text-2xl text-ink mb-5">Pacchetti & prezzi</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {packages.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl p-6" data-testid={`admin-package-${p.name.toLowerCase()}`}>
                  <div className="flex justify-between items-start">
                    <h3 className="font-display font-bold text-xl text-ink">{p.name}</h3>
                    <button onClick={() => setPackageModal(p)} data-testid={`admin-edit-package-${p.name.toLowerCase()}`} className="p-1.5 text-ink/50 hover:text-coral"><Pencil size={16} /></button>
                  </div>
                  <div className="mt-2 font-bold text-coral">{p.price || "Su richiesta"}</div>
                  <p className="mt-1 text-sm text-ink/50">{p.tagline}</p>
                  <ul className="mt-3 space-y-1 text-sm text-ink/60">
                    {p.features.map((f, idx) => <li key={idx}>· {f}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRICING */}
        {tab === "pricing" && <PricingEditor />}

        {/* LEADS */}
        {tab === "leads" && (
          <div data-testid="admin-leads">
            <h2 className="font-display font-bold text-2xl text-ink mb-2">Lead dal chatbot</h2>
            <p className="text-ink/50 text-sm mb-5">Contatti raccolti automaticamente da "Marco" quando i clienti chiedono la bozza gratuita.</p>
            {leads.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center text-ink/50">Nessun lead raccolto dal chatbot.</div>
            ) : (
              <div className="space-y-4">
                {leads.map((l) => (
                  <div key={l.id} className="bg-white rounded-2xl p-6" data-testid={`admin-lead-${l.id}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex flex-wrap gap-x-6 gap-y-1">
                        {l.email && (
                          <a href={`mailto:${l.email}`} className="font-semibold text-ink hover:text-coral" data-testid={`lead-email-${l.id}`}>✉ {l.email}</a>
                        )}
                        {l.phone && (
                          <a href={`tel:${l.phone.replace(/\s/g, "")}`} className="font-semibold text-ink hover:text-coral" data-testid={`lead-phone-${l.id}`}>📞 {l.phone}</a>
                        )}
                        {!l.email && !l.phone && <span className="text-ink/40">Dati parziali</span>}
                      </div>
                      <button onClick={() => deleteLead(l.id)} data-testid={`admin-delete-lead-${l.id}`} className="p-2 text-ink/40 hover:text-coral shrink-0"><Trash2 size={18} /></button>
                    </div>
                    {l.conversation && (
                      <pre className="mt-3 text-ink/60 text-sm whitespace-pre-wrap font-sans bg-beige/50 rounded-xl p-4 max-h-48 overflow-y-auto">{l.conversation}</pre>
                    )}
                    <div className="mt-2 text-xs text-ink/30">{new Date(l.updated_at || l.created_at).toLocaleString("it-IT")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CONTACTS */}
        {tab === "contacts" && (
          <div data-testid="admin-contacts">
            <h2 className="font-display font-bold text-2xl text-ink mb-5">Messaggi ricevuti</h2>
            {contacts.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center text-ink/50">Nessun messaggio ricevuto.</div>
            ) : (
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} className="bg-white rounded-2xl p-6" data-testid={`admin-contact-${c.id}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <div className="font-semibold text-ink">{c.name} <span className="text-ink/40 font-normal">· {c.email}</span></div>
                        <div className="text-xs text-ink/40">{c.phone} {c.service && `· ${c.service}`}</div>
                      </div>
                      <button onClick={() => deleteContact(c.id)} data-testid={`admin-delete-contact-${c.id}`} className="p-2 text-ink/40 hover:text-coral"><Trash2 size={18} /></button>
                    </div>
                    <p className="mt-3 text-ink/70 text-sm">{c.message}</p>
                    <div className="mt-2 text-xs text-ink/30">{new Date(c.created_at).toLocaleString("it-IT")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {projectModal && <ProjectModal project={projectModal.id ? projectModal : null} onClose={() => setProjectModal(null)} onSaved={loadAll} />}
      {packageModal && <PackageModal pkg={packageModal} onClose={() => setPackageModal(null)} onSaved={loadAll} />}
      {pwModal && <ChangePasswordModal onClose={() => setPwModal(false)} />}
    </div>
  );
}
